import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  AcademicCapIcon,
  UserIcon,
  PencilSquareIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  LinkIcon,
  BookOpenIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';

const TrainingSessionDetails = ({ session, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState('details');
  
  // Formatage des dates et heures
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };
  
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };
  
  // Durée totale
  const getTotalDuration = () => {
    if (!session.startDateTime || !session.endDateTime) return '';
    
    const start = new Date(session.startDateTime);
    const end = new Date(session.endDateTime);
    const durationMs = end - start;
    
    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    let result = '';
    if (days > 0) result += `${days} jour${days > 1 ? 's' : ''} `;
    if (hours > 0) result += `${hours}h `;
    if (minutes > 0) result += `${minutes}min`;
    
    return result.trim();
  };

  // État de la session
  const getSessionStatus = () => {
    if (!session.startDateTime || !session.endDateTime) return { text: 'Non défini', color: 'gray' };
    
    const now = new Date();
    const start = new Date(session.startDateTime);
    const end = new Date(session.endDateTime);
    
    if (now < start) {
      return { text: 'À venir', color: 'blue' };
    } else if (now >= start && now <= end) {
      return { text: 'En cours', color: 'green' };
    } else {
      return { text: 'Terminée', color: 'gray' };
    }
  };
  
  const status = getSessionStatus();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm dark:bg-black/75 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-slate-200 dark:border-slate-600 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-600">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-50 flex items-center">
            <AcademicCapIcon className="h-6 w-6 mr-2" />
            Détails de la Session
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800/80 rounded-full transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-slate-900 dark:text-slate-50" />
          </button>
        </div>
        
        {/* En-tête avec statut */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-600">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">
                {session.title}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 flex items-center">
                <AcademicCapIcon className="h-4 w-4 mr-2" />
                {session.moduleName}
              </p>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-sm font-medium bg-${status.color}-500/20 text-${status.color}-400`}>
              {status.text}
            </div>
          </div>
        </div>
        
        {/* Onglets */}
        <div className="flex border-b border-slate-200 dark:border-slate-600">
          <button
            onClick={() => setActiveTab('details')}
            className={`flex-1 py-3 px-4 text-center font-medium ${
              activeTab === 'details' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
            }`}
          >
            Détails
          </button>
          <button
            onClick={() => setActiveTab('resources')}
            className={`flex-1 py-3 px-4 text-center font-medium ${
              activeTab === 'resources' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
            }`}
          >
            Ressources
          </button>
          <button
            onClick={() => setActiveTab('students')}
            className={`flex-1 py-3 px-4 text-center font-medium ${
              activeTab === 'students' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 hover:bg-slate-100/80 dark:bg-slate-800/40'
            }`}
          >
            Étudiants
          </button>
        </div>
        
        {/* Contenu des onglets - avec défilement */}
        <div className="overflow-y-auto p-6 flex-grow">
          {/* Détails */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Dates et durée */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <CalendarDaysIcon className="h-5 w-5 text-blue-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Date de début</h4>
                  </div>
                  <p className="text-slate-900 dark:text-slate-50/90">{formatDate(session.startDateTime)}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{formatTime(session.startDateTime)}</p>
                </div>
                
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <CalendarDaysIcon className="h-5 w-5 text-red-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Date de fin</h4>
                  </div>
                  <p className="text-slate-900 dark:text-slate-50/90">{formatDate(session.endDateTime)}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{formatTime(session.endDateTime)}</p>
                </div>
              </div>
              
              {/* Durée totale */}
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                <div className="flex items-center space-x-2 mb-2">
                  <ClockIcon className="h-5 w-5 text-green-400" />
                  <h4 className="font-medium text-slate-900 dark:text-slate-50">Durée totale</h4>
                </div>
                <p className="text-slate-900 dark:text-slate-50/90 text-lg font-semibold">{getTotalDuration()}</p>
              </div>
              
              {/* Infos pratiques */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPinIcon className="h-5 w-5 text-red-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Lieu</h4>
                  </div>
                  <p className="text-slate-900 dark:text-slate-50/90">{session.location}</p>
                </div>
                
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserGroupIcon className="h-5 w-5 text-amber-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Capacité</h4>
                  </div>
                  <p className="text-slate-900 dark:text-slate-50/90">
                    <span className="text-lg font-semibold">{session.enrolledStudents?.length || 0}</span>
                    <span className="text-slate-600 dark:text-slate-400"> / {session.capacity}</span>
                    <span className="text-slate-600 dark:text-slate-400 text-sm ml-2">places réservées</span>
                  </p>
                </div>
              </div>
              
              {/* Formateur et prix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserIcon className="h-5 w-5 text-blue-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Formateur</h4>
                  </div>
                  <p className="text-slate-900 dark:text-slate-50/90">{session.instructorName}</p>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">ID: {session.instructorId}</p>
                </div>
                
                {session.price && (
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <CurrencyDollarIcon className="h-5 w-5 text-green-400" />
                      <h4 className="font-medium text-slate-900 dark:text-slate-50">Prix</h4>
                    </div>
                    <p className="text-slate-900 dark:text-slate-50/90 text-lg font-semibold">{session.price} DA</p>
                  </div>
                )}
              </div>
              
              {/* Description */}
              {session.description && (
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center space-x-2 mb-2">
                    <DocumentTextIcon className="h-5 w-5 text-purple-400" />
                    <h4 className="font-medium text-slate-900 dark:text-slate-50">Description</h4>
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{session.description}</p>
                </div>
              )}
              
              {/* Prérequis et équipement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {session.prerequisites && (
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <BookOpenIcon className="h-5 w-5 text-amber-400" />
                      <h4 className="font-medium text-slate-900 dark:text-slate-50">Prérequis</h4>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300">{session.prerequisites}</p>
                  </div>
                )}
                
                {session.equipment && (
                  <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center space-x-2 mb-2">
                      <ArchiveBoxIcon className="h-5 w-5 text-orange-400" />
                      <h4 className="font-medium text-slate-900 dark:text-slate-50">Équipement requis</h4>
                    </div>
                    <p className="text-slate-700 dark:text-slate-300">{session.equipment}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Ressources */}
          {activeTab === 'resources' && (
            <div className="space-y-6">
              {(!session.resources || session.resources.length === 0) ? (
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600 text-center">
                  <DocumentTextIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-1">Aucune ressource disponible</h3>
                  <p className="text-slate-600 dark:text-slate-400">Aucune ressource pédagogique n'a été ajoutée à cette session.</p>
                </div>
              ) : (
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {session.resources.map((resource) => (
                      <div key={resource.id} className="flex items-center p-4 hover:bg-slate-100/80 dark:bg-slate-800/40">
                        {/* Icon */}
                        <div className="mr-4">
                          {resource.type === 'document' && <DocumentTextIcon className="h-8 w-8 text-blue-400" />}
                          {resource.type === 'video' && <ComputerDesktopIcon className="h-8 w-8 text-red-400" />}
                          {resource.type === 'exercise' && <PencilSquareIcon className="h-8 w-8 text-green-400" />}
                          {resource.type === 'quiz' && <DocumentTextIcon className="h-8 w-8 text-amber-400" />}
                          {resource.type === 'link' && <LinkIcon className="h-8 w-8 text-purple-400" />}
                        </div>
                        
                        {/* Details */}
                        <div className="flex-1">
                          <h4 className="text-slate-900 dark:text-slate-50 font-medium">{resource.name}</h4>
                          <p className="text-slate-600 dark:text-slate-400 text-sm truncate">{resource.url}</p>
                        </div>
                        
                        {/* Action */}
                        <div>
                          <a 
                            href={resource.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="p-2 bg-slate-50 dark:bg-slate-800/80 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-900 dark:text-slate-50 transition-colors"
                          >
                            <LinkIcon className="h-5 w-5" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Étudiants */}
          {activeTab === 'students' && (
            <div className="space-y-6">
              {(!session.enrolledStudents || session.enrolledStudents.length === 0) ? (
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600 text-center">
                  <UserGroupIcon className="h-12 w-12 text-slate-900 dark:text-slate-50/20 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-1">Aucun étudiant inscrit</h3>
                  <p className="text-slate-600 dark:text-slate-400">Aucun étudiant n'est inscrit à cette session de formation.</p>
                </div>
              ) : (
                <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
                  <div className="divide-y divide-slate-200 dark:divide-slate-700">
                    {/* Entête du tableau */}
                    <div className="grid grid-cols-3 p-3 bg-slate-100/80 dark:bg-slate-800/40">
                      <div className="text-slate-600 dark:text-slate-400 font-medium text-sm">Nom</div>
                      <div className="text-slate-600 dark:text-slate-400 font-medium text-sm">Statut</div>
                      <div className="text-slate-600 dark:text-slate-400 font-medium text-sm">Date d'inscription</div>
                    </div>
                    
                    {/* Liste des étudiants */}
                    {session.enrolledStudents.map((student) => (
                      <div key={student.id} className="grid grid-cols-3 p-3 hover:bg-slate-100/80 dark:bg-slate-800/40">
                        <div className="text-slate-900 dark:text-slate-50">{student.name}</div>
                        <div>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                            Confirmé
                          </span>
                        </div>
                        <div className="text-slate-600 dark:text-slate-400">
                          {new Date(student.enrollmentDate).toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Capacité et progression */}
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 border border-slate-200 dark:border-slate-600">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium text-slate-900 dark:text-slate-50 flex items-center">
                    <UserGroupIcon className="h-5 w-5 mr-2 text-blue-400" />
                    Places réservées
                  </h4>
                  <span className="text-slate-900 dark:text-slate-50/90">
                    {session.enrolledStudents?.length || 0} / {session.capacity}
                  </span>
                </div>
                <div className="w-full bg-slate-50 dark:bg-slate-800/80 h-2 rounded-full">
                  <div 
                    className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-400"
                    style={{ width: `${(session.enrolledStudents?.length || 0) / session.capacity * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-600">
          <div className="flex justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              Fermer
            </button>
            
            <button
              onClick={() => onEdit(session)}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 transition-all flex items-center space-x-2"
            >
              <PencilSquareIcon className="h-5 w-5" />
              <span>Modifier</span>
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TrainingSessionDetails;